export interface WeatherData {
  temp: number
  feelsLike: number
  humidity: number
  description: string
  icon: string
  windSpeed: number
}

export interface WeatherForecast {
  current: WeatherData
  forecast: Array<{
    date: string
    temp_max: number
    temp_min: number
    humidity: number
    description: string
    icon: string
  }>
}

// Weather icons mapping
const weatherIcons: Record<string, string> = {
  'clear': '☀️',
  'clouds': '☁️',
  'rain': '🌧️',
  'drizzle': '🌦️',
  'thunderstorm': '⛈️',
  'snow': '❄️',
  'mist': '🌫️',
  'smoke': '💨',
  'haze': '🌫️',
  'dust': '🌪️',
  'fog': '🌫️',
  'sand': '🌪️',
  'ash': '🌋',
  'squall': '💨',
  'tornado': '🌪️',
  'night_clear': '🌙',
  'night_clouds': '🌙',
}

export function getWeatherIcon(main: string, isNight?: boolean): string {
  const key = isNight ? `night_${main.toLowerCase()}` : main.toLowerCase()
  return weatherIcons[key] || '🌤️'
}

export async function fetchWeather(
  latitude: number,
  longitude: number,
  cityName: string
): Promise<WeatherForecast | null> {
  try {
    // Using Open-Meteo API (free, no API key required)
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,relative_humidity_2m_max&timezone=auto`
    )

    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status}`)
    }

    const data = await response.json()

    const current = data.current
    const daily = data.daily

    // Convert WMO weather codes to descriptions
    const getWeatherDescription = (code: number): string => {
      if (code === 0) return 'صاف'
      if (code === 1 || code === 2) return 'ابری'
      if (code === 3) return 'ابری'
      if (code === 45 || code === 48) return 'مه'
      if (code === 51 || code === 53 || code === 55) return 'بارانی سبک'
      if (code === 61 || code === 63 || code === 65) return 'بارانی'
      if (code === 71 || code === 73 || code === 75) return 'برفانی'
      if (code === 80 || code === 81 || code === 82) return 'طوفان'
      if (code === 85 || code === 86) return 'برفانی سنگین'
      if (code === 95 || code === 96 || code === 99) return 'رعد و برق'
      return 'نامشخص'
    }

    const forecast = daily.time.map((date: string, index: number) => ({
      date,
      temp_max: daily.temperature_2m_max[index],
      temp_min: daily.temperature_2m_min[index],
      humidity: daily.relative_humidity_2m_max[index],
      description: getWeatherDescription(daily.weather_code[index]),
      icon: getWeatherIcon(getWeatherDescription(daily.weather_code[index])),
    }))

    return {
      current: {
        temp: Math.round(current.temperature_2m),
        feelsLike: Math.round(current.temperature_2m),
        humidity: current.relative_humidity_2m,
        description: getWeatherDescription(current.weather_code),
        icon: getWeatherIcon(getWeatherDescription(current.weather_code)),
        windSpeed: Math.round(current.wind_speed_10m),
      },
      forecast: forecast.slice(0, 3), // 3-day forecast
    }
  } catch (error) {
    console.error(`Failed to fetch weather for ${cityName}:`, error)
    return null
  }
}

export function getCityCoordinates(cityName: string): Promise<{ lat: number; lon: number } | null> {
  // This would need to use a geocoding API
  // For now, returning popular cities' coordinates
  const cities: Record<string, { lat: number; lon: number }> = {
    تهران: { lat: 35.6892, lon: 51.389 },
    دبی: { lat: 25.2048, lon: 55.2708 },
    توکیو: { lat: 35.6762, lon: 139.6503 },
    لندن: { lat: 51.5074, lon: -0.1278 },
    نیویورک: { lat: 40.7128, lon: -74.006 },
    سیدنی: { lat: -33.8688, lon: 151.2093 },
    singapore: { lat: 1.3521, lon: 103.8198 },
    bangkok: { lat: 13.7563, lon: 100.5018 },
    'tehran': { lat: 35.6892, lon: 51.389 },
    'dubai': { lat: 25.2048, lon: 55.2708 },
    'tokyo': { lat: 35.6762, lon: 139.6503 },
    'london': { lat: 51.5074, lon: -0.1278 },
    'newyork': { lat: 40.7128, lon: -74.006 },
    'sydney': { lat: -33.8688, lon: 151.2093 },
    'paris': { lat: 48.8566, lon: 2.3522 },
    'berlin': { lat: 52.52, lon: 13.405 },
    'moscow': { lat: 55.7558, lon: 37.6173 },
    'istanbul': { lat: 41.0082, lon: 28.9784 },
  }

  const normalized = cityName.toLowerCase().replace(/\s+/g, '')
  const coords = cities[normalized]

  if (coords) {
    return Promise.resolve(coords)
  }

  // For unknown cities, return null (could be expanded with geocoding API)
  return Promise.resolve(null)
}
